<template>
  <div class="agila-date-picker">
    <label v-if="props.label">{{ props.label }}</label>
    <agila-form-error :error="error" v-if="props.showError"></agila-form-error>
    <div class="date-picker-border" :class="{ 'input-error': !!error }">
      <select
        name="dropdownDay"
        id="dropdownDay"
        :aria-label="'Geburtstag:Tag'"
        :tabindex="0"
        data-cy="birthday"
        class="date-picker-day"
        :class="{ 'placeholder-color': selectedDay === 'Tag' }"
        @change="calculateDate()"
        v-model="selectedDay"
      >
        <option value="Tag" disabled selected>Tag</option>
        <option
          v-for="option in dayOptions"
          :value="option"
          v-bind:key="option"
        >
          {{ option }}
        </option>
      </select>
      <select
        name="dropdownMonth"
        id="dropdownMonth"
        :aria-label="'Geburtstag:Monat'"
        :tabindex="0"
        data-cy="birthmonth"
        class="date-picker-month"
        :class="{ 'placeholder-color': selectedMonth === 'Monat' }"
        @change="calculateDate()"
        v-model="selectedMonth"
      >
        <option value="Monat" disabled selected>Monat</option>
        <option
          v-for="option in monthOptions"
          :value="option"
          v-bind:key="option"
        >
          {{ option }}
        </option>
      </select>
      <select
        name="dropdownYear"
        id="dropdownYear"
        :aria-label="'Geburtstag:Jahr'"
        :tabindex="0"
        data-cy="birthyear"
        class="date-picker-year"
        :class="{ 'placeholder-color': selectedYear === 'Jahr' }"
        @change="calculateDate()"
        v-model="selectedYear"
      >
        <option value="Jahr" disabled selected>Jahr</option>
        <option
          v-for="option in yearOptions"
          :value="option"
          v-bind:key="option"
        >
          {{ option }}
        </option>
      </select>
    </div>
  </div>
</template>

<script setup lang="ts">
import {
  defineEmits,
  defineExpose,
  defineProps,
  ref,
  watch,
  withDefaults,
} from "vue";
import AgilaFormError from "./agilaFormError.vue";
import useSharedFormLogic from "../helper/functions/useSharedFormLogic";
import { PRIMEVUE_CONFIG } from "../store/primevue";

interface Props {
  minDate?: Date | string;
  maxDate?: Date | string;
  scrollHeight?: string;
  label?: string;
  selectedDate?: Date;
  showError?: boolean;
  rules?: string[];
}

const props = withDefaults(defineProps<Props>(), {
  minDate: undefined,
  maxDate: undefined,
  scrollHeight: "394px",
  label: "",
  selectedDate: undefined,
  showError: false,
  rules: () => [],
});

const emit = defineEmits<{
  (e: "update:selectedDate", selectedDate: Date | undefined): void;
  (e: "selectDate", selectedDate: Date | undefined): void;
  (e: "update:modelValue", selectedDate: Date | undefined): void;
  (e: "error", error: string): void;
}>();

const { readyForCheck, inputValue, error, updateValue, validate } =
  useSharedFormLogic(props, emit);

defineExpose({
  validate: () => {
    readyForCheck.value = true;
    return validate(selectedDate.value);
  },
});

const dateToday: Date = new Date();
const minDate = ref<Date | undefined>(
  props.minDate
    ? typeof props.minDate === "string"
      ? new Date(props.minDate)
      : props.minDate
    : undefined
);
const maxDate = ref<Date | undefined>(
  props.maxDate
    ? typeof props.maxDate === "string"
      ? new Date(props.maxDate)
      : props.maxDate
    : undefined
);
const minYear = ref<number>(
  minDate.value ? minDate.value.getFullYear() : dateToday.getFullYear() - 100
);
const maxYear = ref<number>(
  maxDate.value ? maxDate.value.getFullYear() : dateToday.getFullYear()
);
const dayOptions = ref<string[]>(createDaysArray(31));
const monthOptions = ref<string[]>(PRIMEVUE_CONFIG.locale.monthNames);
const yearOptions = ref<string[]>(
  Array.from({ length: maxYear.value - minYear.value + 1 }, (_, index) =>
    (maxYear.value - index).toString()
  )
);
const selectedDate = ref<Date | undefined>(props.selectedDate);
const selectedDay = ref<string>(
  selectedDate.value?.getDate().toString() || "Tag"
);
const selectedMonth = ref<string>(
  selectedDate.value
    ? monthOptions.value[Number(selectedDate.value?.getMonth())]
    : "Monat"
);
const selectedYear = ref<string>(
  selectedDate.value?.getFullYear().toString() || "Jahr"
);

function createDaysArray(days: number, start = 1): Array<string> {
  return Array.from({ length: days }, (_, index) => (index + start).toString());
}

function calculateDate(): void {
  if (selectedMonth.value) {
    const lastDayOfMonth = new Date(
      Number(selectedYear.value) || dateToday.getFullYear(),
      monthOptions.value.indexOf(selectedMonth.value) + 1,
      0
    ).getDate();
    const diff =
      Number(dayOptions.value[dayOptions.value.length - 1]) - lastDayOfMonth;

    if (diff > 0) {
      dayOptions.value = dayOptions.value.slice(0, diff * -1);
    } else {
      dayOptions.value = createDaysArray(31);
    }

    if (selectedDay.value && Number(selectedDay.value) > lastDayOfMonth) {
      selectedDay.value = lastDayOfMonth.toString();
    }
  }

  if (
    selectedDay.value &&
    selectedMonth.value &&
    selectedYear.value &&
    selectedDay.value !== "Tag" &&
    selectedMonth.value !== "Monat" &&
    selectedYear.value !== "Jahr"
  ) {
    selectedDate.value = new Date(
      Number(selectedYear.value),
      monthOptions.value.indexOf(selectedMonth.value),
      Number(selectedDay.value)
    );
    setDate();
  }
}

function setDate(): void {
  if (selectedDate.value) {
    validate(selectedDate.value);
    emit("update:selectedDate", selectedDate.value);
    emit("selectDate", selectedDate.value);
  }
}

function setYearOptions(): void {
  yearOptions.value = Array.from(
    { length: maxYear.value - minYear.value + 1 },
    (_, index) => (maxYear.value - index).toString()
  );
}

function resetDate(): void {
  selectedDay.value = undefined;
  selectedMonth.value = undefined;
  selectedYear.value = undefined;
  emit("update:selectedDate", undefined);
  emit("selectDate", undefined);
}

function setDayMonthYear(): void {
  selectedDay.value = selectedDate.value?.getDate().toString() || "Tag";
  selectedMonth.value = selectedDate.value
    ? monthOptions.value[Number(selectedDate.value?.getMonth())]
    : "Monat";
  selectedYear.value = selectedDate.value?.getFullYear().toString() || "Jahr";
}

watch(
  () => props.minDate,
  () => {
    minDate.value = props.minDate
      ? typeof props.minDate === "string"
        ? new Date(props.minDate)
        : props.minDate
      : undefined;
    minYear.value = minDate.value
      ? minDate.value.getFullYear()
      : dateToday.getFullYear() - 100;
    setYearOptions();
    if (
      minDate.value &&
      selectedDate.value &&
      selectedDate.value < minDate.value
    ) {
      resetDate();
    }
  }
);
watch(
  () => props.maxDate,
  () => {
    maxDate.value = props.maxDate
      ? typeof props.maxDate === "string"
        ? new Date(props.maxDate)
        : props.maxDate
      : undefined;
    maxYear.value = maxDate.value
      ? maxDate.value.getFullYear()
      : dateToday.getFullYear();
    setYearOptions();
    if (
      maxDate.value &&
      selectedDate.value &&
      selectedDate.value > maxDate.value
    ) {
      resetDate();
    }
  }
);

watch(
  () => props.selectedDate,
  () => {
    if (props.selectedDate !== selectedDate.value) {
      selectedDate.value = props.selectedDate;
    }
    setDayMonthYear();
  }
);
</script>

<style scoped lang="scss">
.agila-date-picker {
  margin-bottom: 20px;

  label {
    font-family: var(--font-open-sans-regular), sans-serif;
    color: var(--color-gray-700);
  }

  .date-picker-border {
    border: 1px solid var(--color-gray-700);
    box-shadow: 0 0 0 1px transparent;
    border-radius: 6px;
    background-color: var(--color-white);
    display: flex;
    margin-top: 8px;
    width: fit-content;

    select {
      text-align: center;
      height: 46px;
      border: 0;
      border-radius: 6px;
      margin-right: 4px;
      appearance: none;
      -webkit-appearance: none;
      -moz-appearance: none;
      background-image: url("data:image/svg+xml,%3Csvg width='14' height='14' viewBox='0 0 14 14' fill='none' xmlns='http://www.w3.org/2000/svg' class='p-icon p-dropdown-trigger-icon' aria-hidden='true' data-pc-section='dropdownicon'%3E%3Cpath d='M7.01744 10.398C6.91269 10.3985 6.8089 10.378 6.71215 10.3379C6.61541 10.2977 6.52766 10.2386 6.45405 10.1641L1.13907 4.84913C1.03306 4.69404 0.985221 4.5065 1.00399 4.31958C1.02276 4.13266 1.10693 3.95838 1.24166 3.82747C1.37639 3.69655 1.55301 3.61742 1.74039 3.60402C1.92777 3.59062 2.11386 3.64382 2.26584 3.75424L7.01744 8.47394L11.769 3.75424C11.9189 3.65709 12.097 3.61306 12.2748 3.62921C12.4527 3.64535 12.6199 3.72073 12.7498 3.84328C12.8797 3.96582 12.9647 4.12842 12.9912 4.30502C13.0177 4.48162 12.9841 4.662 12.8958 4.81724L7.58083 10.1322C7.50996 10.2125 7.42344 10.2775 7.32656 10.3232C7.22968 10.3689 7.12449 10.3944 7.01744 10.398Z' fill='%236b7280'%3E%3C/path%3E%3C/svg%3E");
      background-size: 1rem;
      background-position: right 6px center;
      background-repeat: no-repeat;
      background-color: var(--color-white);
      padding: 0 28px 0 6px;
      font-family: var(--font-open-sans-regular), sans-serif;
      color: var(--color-gray-700);
      font-size: var(--font-size);
      cursor: pointer;
      box-sizing: content-box;

      &.date-picker-day {
        min-width: 39px;
      }
      &.date-picker-month {
        min-width: 95px;
      }
      &.date-picker-year {
        min-width: 49px;
      }
      &:nth-of-type(3) {
        margin-right: 0;
      }
    }
    &:hover:not(:focus):not(.input-error) {
      border: 1px solid var(--color-blue-600);
      box-shadow: 0 0 0 1px var(--color-blue-600);
    }
    .placeholder-color {
      color: var(--color-gray-500);
    }
    &.input-error {
      border: 1px solid var(--color-red-700);
      box-shadow: 0 0 0 1px var(--color-red-700);
    }
  }
}
</style>
